import React, { useState } from 'react';
import { useAppointmentDetails } from '../organisms/ReviewWrapper';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import PoweredByApruv from '../common/PoweredByApruv';
import googleIcon from '../../assets/images/google.svg';
import facebookIcon from '../../assets/images/facebook.svg';
import './TipTech.css';

interface TipTechProps {
  isOldReview?: boolean;
  onStepChange?: (step: string) => void;
}

const TipTech: React.FC<TipTechProps> = ({ isOldReview = false, onStepChange }) => {
  const appointmentDetails = useAppointmentDetails(); // Get from context
  const [loading, setLoading] = useState(false);

  const getSubheaderText = (): string => {
    const rateConfig = apruvRateService.getRateConfig();
    if (!rateConfig?.TechBonusSubHeader || !rateConfig?.TechBonusAmount) return '';
    
    return rateConfig.TechBonusSubHeader.replace('{{TipAmount}}', `$${rateConfig.TechBonusAmount}`);
  };

  const handleReview = async (link: 'google' | 'yelp' | 'facebook' | 'bbb') => {
    if (!appointmentDetails) return;

    try {
      setLoading(true);
      await reviewService.clickReview(appointmentDetails.appointment_details.AppointmentId);

      let url = '';
      switch (link) {
        case 'google':
          url = appointmentDetails.appointment_details.GoogleLink;
          break;
        case 'yelp':
          url = appointmentDetails.appointment_details.YelpLink;
          break;
        case 'facebook':
          url = appointmentDetails.appointment_details.FacebookLink;
          break;
        case 'bbb':
          url = appointmentDetails.appointment_details.BBBLink;
          break;
      }

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error clicking review:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!appointmentDetails) {
    return (
      <div className="tip-tech-container">
        <div className="tip-tech-loading">Loading...</div>
        <PoweredByApruv sx={{ mt: 'auto' }} />
      </div>
    );
  }

  const rateConfig = apruvRateService.getRateConfig();

  return (
    <div className="tip-tech-container">
      <div className="tip-tech-content">
        {/* Header Section */}
        <div className="tip-tech-header">
          <h1 className="tip-tech-title">
            Thank you for your tip!
          </h1>
          <p className="tip-tech-subtitle">
            {getSubheaderText()}
          </p>
        </div>

        {/* Technician Info */}
        <div className="tip-tech-info">
          <div className="tip-tech-info-card">
            <h3 className="tip-tech-info-title">
              Your technician {appointmentDetails.appointment_details.TechnicianFirstName} will receive:
            </h3>
            <div className="tip-tech-bonus-amount">
              ${rateConfig?.TechBonusAmount || 0}
            </div>
            <p className="tip-tech-info-description">
              As a bonus from {appointmentDetails.appointment_details.CompanyName}
            </p>
          </div>
        </div>

        {/* Review Platforms */}
        <div className="tip-tech-review-section">
          <h3 className="tip-tech-review-title">
            Help others by leaving a review
          </h3>
          
          <div className="tip-tech-review-buttons">
            {/* Google Review */}
            {appointmentDetails.appointment_details.GoogleLink && (
              <button
                onClick={() => handleReview('google')}
                disabled={loading}
                className="tip-tech-review-button google"
              >
                <img src={googleIcon} alt="Google" />
                <span>Review on Google</span>
              </button>
            )}

            {/* Facebook Review */}
            {appointmentDetails.appointment_details.FacebookLink && (
              <button
                onClick={() => handleReview('facebook')}
                disabled={loading}
                className="tip-tech-review-button facebook"
              >
                <img src={facebookIcon} alt="Facebook" />
                <span>Review on Facebook</span>
              </button>
            )}

            {/* Yelp Review */}
            {appointmentDetails.appointment_details.YelpLink && (
              <button
                onClick={() => handleReview('yelp')}
                disabled={loading}
                className="tip-tech-review-button yelp"
              >
                <span>Review on Yelp</span>
              </button>
            )}

            {/* BBB Review */}
            {appointmentDetails.appointment_details.BBBLink && (
              <>
                {appointmentDetails.appointment_details.GoogleLink && (
                  <p className="tip-tech-bbb-message">
                    If you've already left your feedback on Google, please consider leaving a review on our Better Business Bureau link.
                  </p>
                )}
                <button
                  onClick={() => handleReview('bbb')}
                  disabled={loading}
                  className="tip-tech-review-button bbb"
                >
                  <span>Review on BBB</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Company Contact */}
        <div className="tip-tech-contact">
          <div className="tip-tech-contact-card">
            <h4 className="tip-tech-contact-title">
              {appointmentDetails.appointment_details.CompanyName}
            </h4>
            {appointmentDetails.appointment_details.CompanyPhone && (
              <a 
                href={`tel:${appointmentDetails.appointment_details.CompanyPhone}`}
                className="tip-tech-contact-phone"
              >
                {appointmentDetails.appointment_details.CompanyPhone}
              </a>
            )}
          </div>
        </div>

        {/* Footer Message */}
        <div className="tip-tech-footer">
          <p>
            Thank you for choosing {appointmentDetails.appointment_details.CompanyName}. 
            Your feedback and support help us continue providing excellent service.
          </p>
        </div>

        <PoweredByApruv />
      </div>
    </div>
  );
};

export default TipTech;
